import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';
import { Dialog, DialogContent, DialogTitle, Tooltip as MuiTooltip } from '@material-ui/core';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';

import ConfigurableDataTable from 'app/admin/common/component/ConfigurableDataTable';
import DateRangeToolbar from 'app/admin/common/component/DateRangeToolbar';
import { Button } from './Pipeline';

import { useOptimizationPipeline } from '../OptimizationPipelineProvider';

function safeDate(v) {
    if (!v) return null;
    if (v instanceof Date) return v;
    if (typeof v === 'number' && Number.isFinite(v)) return new Date(v);
    if (typeof v === 'string') {
        const m = v.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,6}))?$/);
        if (m) {
            const [, Y, Mo, D, H, Mi, S] = m;
            const utcMs = Date.UTC(Number(Y), Number(Mo) - 1, Number(D), Number(H), Number(Mi), Number(S), 0);
            return new Date(utcMs);
        }
        const d = new Date(v);
        if (!Number.isNaN(d.getTime())) return d;
    }
    return null;
}
const fmt = (v) => {
    const d = safeDate(v);
    return d ? format(d, 'yyyy.MM.dd', { locale: hu }) : '-';
};

export default function DataValidationHistoryPopup() {
    const [outlierData, setOutlierData] = useState([]);
    const [detectorNames, setDetectorNames] = useState([]);

    // Outlier filter: 'all' | 'has' | 'yes' | 'no'
    const [outlierView, setOutlierView] = useState('has');

    const [minDate, setMinDate] = useState(null);
    const [maxDate, setMaxDate] = useState(null);
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);

    const { setPopup, cleanMeasurementData, mergedMeasurementDataList } = useOptimizationPipeline();
    const mainTheme = useSelector(selectMainTheme);

    function collectOutlierData(list) {
        const out = [];
        for (const entry of (Array.isArray(list) ? list : [])) {
            const ts = entry?.ts;
            const od = entry?.meta?.outlier_detection;
            const row = { ts, outlier: od?.outlier, detectors: {} };

            const details = Array.isArray(od?.details) ? od.details : [];
            for (const d of details) {
                const name = d?.outlier_detector_name || 'unknown';
                if (!row.detectors[name]) {
                    row.detectors[name] = { name, version: d?.outlier_detector_version, prediction: d?.prediction };
                }
            }
            out.push(row);
        }
        return out;
    }

    function getAllDetectorNames(data) {
        const set = new Set();
        for (const row of (Array.isArray(data) ? data : [])) {
            const keys = row?.detectors ? Object.keys(row.detectors) : [];
            keys.forEach(k => set.add(k));
        }
        return Array.from(set);
    }

    function coloredOutlierCell(row, content) {
        const o = row?.outlier;
        const bg = o === true ? 'bg-red-100 text-red-900' : o === false ? 'bg-green-100 text-green-900' : 'bg-gray-100 text-gray-500';
        const dim = o === true || o === false ? '' : ' opacity-70';
        return (<div className={'flex flex-col text-12 px-5 py-3 rounded font-semibold ' + bg + dim}>{content ?? '-'}</div>);
    }

    function detectorCell(row, name) {
        const d = row?.detectors?.[name];
        const text = d?.prediction === true ? 'Yes' : d?.prediction === false ? 'No' : '-';
        return (
            <div className='flex flex-col text-12 px-5 py-3 rounded bg-gray-100'>
                <div className='leading-4'>
                    <span className='font-semibold'>Prediction:</span> {text}
                </div>
            </div>
        );
    }

    useEffect(() => {
        if (mergedMeasurementDataList) {
            const data = collectOutlierData(mergedMeasurementDataList);
            setOutlierData(data);
            setDetectorNames(getAllDetectorNames(data));
        }
    }, [mergedMeasurementDataList]);

    useEffect(() => {
        if (outlierData?.length > 0) {
            const dates = outlierData
                .map(r => safeDate(r.ts))
                .filter(Boolean)
                .sort((a, b) => a - b);
            if (dates.length > 0) {
                const first = dates[0];
                const last = dates[dates.length - 1];
                setMinDate(first);
                setMaxDate(last);
                setDateFrom(first);
                setDateTo(last);
            } else {
                setMinDate(null);
                setMaxDate(null);
                setDateFrom(null);
                setDateTo(null);
            }
        } else {
            setMinDate(null);
            setMaxDate(null);
            setDateFrom(null);
            setDateTo(null);
        }
    }, [outlierData]);

    const columns = useMemo(() => {
        const base = [
            {
                key: 'ts',
                header: 'Timestamp',
                sortable: true,
                format: (v) => fmt(v),
            },
            {
                key: 'outlier',
                header: 'Outlier',
                accessor: (row) => coloredOutlierCell(row, row.outlier === true ? 'Yes' : row.outlier === false ? 'No' : '-'),
            },
        ];
        const dynamic = detectorNames.map((name) => ({
            key: name,
            header: name,
            accessor: (row) => detectorCell(row, name),
        }));
        return [...base, ...dynamic];
    }, [detectorNames]);


    const filteredRows = useMemo(() => {
        if (!outlierData?.length) return [];

        let rows = outlierData;

        if (dateFrom && dateTo) {
            const from = dateFrom.getTime();
            const to = dateTo.getTime();
            rows = rows.filter(r => {
                const d = safeDate(r.ts);
                if (!d) return false;
                const t = d.getTime();
                return t >= from && t <= to;
            });
        }

        if (outlierView === 'has') {
            rows = rows.filter(r => r.outlier === true || r.outlier === false);
        } else if (outlierView === 'yes') {
            rows = rows.filter(r => r.outlier === true);
        } else if (outlierView === 'no') {
            rows = rows.filter(r => r.outlier === false);
        }

        return rows;
    }, [outlierData, outlierView, dateFrom, dateTo]);


    const ToolbarChip = ({ label, title, onClick, disabled = false, active = false }) => (
        <MuiTooltip title={title || label} arrow>
            <span>
                <button
                    type='button'
                    onClick={onClick}
                    disabled={disabled}
                    aria-pressed={active}
                    className={
                        'text-10 leading-none font-medium px-6 py-4 rounded border transition-colors ' +
                        (active
                            ? 'bg-gray-200 text-black border-transparent shadow'
                            : 'bg-transparent text-black/70 border-gray-100 hover:bg-gray-50') +
                        (disabled ? ' opacity-40 cursor-not-allowed' : ' focus:outline-none focus:ring-2 focus:ring-blue-300')
                    }
                >
                    {label}
                </button>
            </span>
        </MuiTooltip>
    );

    const OutlierToolbar = () => (
        <div className='flex items-center gap-8'>
            <div className='text-12 opacity-70 mr-4'>Outlier:</div>
            <div className='flex items-center gap-2'>
                <ToolbarChip label='All' title='Show all rows' active={outlierView === 'all'} onClick={() => setOutlierView('all')} />
                <ToolbarChip label='Has data' title='Rows where outlier is Yes or No' active={outlierView === 'has'} onClick={() => setOutlierView('has')} />
                <ToolbarChip label='Yes' title='Only outliers' active={outlierView === 'yes'} onClick={() => setOutlierView('yes')} />
                <ToolbarChip label='No' title='Only non-outliers' active={outlierView === 'no'} onClick={() => setOutlierView('no')} />
            </div>
        </div>
    );

    return (
        <ThemeProvider theme={mainTheme}>

            <Dialog open fullWidth maxWidth={outlierData.length ? 'lg' : 'sm'}>

                <DialogTitle disableTypography className='border-b flex flex-col px-0 py-12'>

                    <div className='flex items-center justify-between gap-32 px-32 py-12'>
                        <div className='flex gap-8'>
                            History | {cleanMeasurementData.name}
                        </div>
                        <Button label='Close' onClick={() => setPopup(null)} />
                    </div>

                    {outlierData.length > 0 && (<>
                        <DateRangeToolbar
                            from={dateFrom}
                            to={dateTo}
                            minDate={minDate}
                            maxDate={maxDate}
                            onChange={({ from, to }) => {
                                setDateFrom(from || null);
                                setDateTo(to || null);
                            }}
                        />
                        <div className='flex items-center justify-between gap-16 px-32'>
                            <OutlierToolbar />
                        </div>
                    </>)}

                </DialogTitle>

                <DialogContent className='w-full flex flex-col p-20 overflow-hidden' style={{ height: outlierData.length ? '520px' : 'auto' }}>
                    <ConfigurableDataTable rows={filteredRows} columns={columns} searchable={false} />
                </DialogContent>

            </Dialog>

        </ThemeProvider>
    );
}
